import { create } from 'zustand';
import { fetchDeezerTrackById } from '../lib/deezer.js';

let audioEl = null;

/** Si lecture échoue (URL expirée, 403…), on retente une fois avec un nouvel appel Deezer. */
let deezerRecoveryTrackId = null;

function ensureAudio(set, get) {
  if (audioEl) return audioEl;
  if (typeof Audio === 'undefined') return null;
  audioEl = new Audio();
  audioEl.preload = 'none';
  audioEl.crossOrigin = 'anonymous';
  audioEl.addEventListener('ended', () => {
    deezerRecoveryTrackId = null;
    set({ status: 'idle', currentId: null, progress: 0 });
  });
  audioEl.addEventListener('pause', () => {
    set((s) => (s.status === 'playing' ? { ...s, status: 'paused' } : s));
  });
  audioEl.addEventListener('play', () => {
    set((s) => ({ ...s, status: 'playing' }));
  });
  audioEl.addEventListener('timeupdate', () => {
    if (!audioEl?.duration) return;
    set({ progress: audioEl.currentTime / audioEl.duration });
  });
  audioEl.addEventListener('error', () => {
    const recoverId = deezerRecoveryTrackId;
    const curId = get().currentId;
    deezerRecoveryTrackId = null;

    if (recoverId && curId) {
      void (async () => {
        const track = await fetchDeezerTrackById(recoverId);
        const nextUrl = track?.preview ?? null;
        if (!nextUrl || get().currentId !== curId || !audioEl) {
          set({ status: 'error', currentId: null, progress: 0 });
          return;
        }
        audioEl.src = nextUrl;
        try {
          await audioEl.play();
        } catch {
          set({ status: 'error', currentId: null, progress: 0 });
        }
      })();
      return;
    }

    set({ status: 'error', currentId: null, progress: 0 });
  });
  return audioEl;
}

export const useAudioPreview = create((set, get) => ({
  status: 'idle', // 'idle' | 'loading' | 'playing' | 'paused' | 'error'
  currentId: null,
  progress: 0,

  async toggle({ id, url, deezerTrackId }) {
    const audio = ensureAudio(set, get);
    if (!audio) return;

    const { currentId, status } = get();

    // Toggle pause/resume sur la piste courante
    if (currentId === id) {
      if (status === 'playing') {
        audio.pause();
      } else if (status === 'paused') {
        try {
          await audio.play();
        } catch {
          // Extrait peut avoir expiré pendant la pause : renouveler via Deezer si possible
          if (deezerTrackId) {
            set({ status: 'loading' });
            const track = await fetchDeezerTrackById(deezerTrackId);
            if (get().currentId !== id) return;
            const nextUrl = track?.preview ?? url ?? null;
            if (nextUrl) {
              deezerRecoveryTrackId = deezerTrackId;
              audio.src = nextUrl;
              try {
                await audio.play();
                return;
              } catch {
                /* fall through */
              }
            }
          }
          deezerRecoveryTrackId = null;
          set({ status: 'error', currentId: null, progress: 0 });
        }
      }
      return;
    }

    // Nouvelle piste : on stoppe la précédente
    audio.pause();
    audio.currentTime = 0;
    deezerRecoveryTrackId = null;
    set({ currentId: id, status: 'loading', progress: 0 });

    let resolvedUrl = null;
    if (deezerTrackId) {
      const track = await fetchDeezerTrackById(deezerTrackId);
      // Un autre tap play est arrivé pendant le fetch (proxy Deezer lent) : abandonner,
      // sinon on jouerait cette piste alors que l'UI affiche l'autre en lecture.
      if (get().currentId !== id) return;
      resolvedUrl = track?.preview ?? null;
    }
    if (!resolvedUrl) {
      resolvedUrl = url ?? null;
    }

    deezerRecoveryTrackId = deezerTrackId ?? null;

    if (!resolvedUrl) {
      deezerRecoveryTrackId = null;
      set({ status: 'error', currentId: null });
      return;
    }

    audio.src = resolvedUrl;
    try {
      await audio.play();
    } catch {
      set({ status: 'error', currentId: null });
    }
  },

  stop() {
    deezerRecoveryTrackId = null;
    if (audioEl) {
      audioEl.pause();
      audioEl.currentTime = 0;
    }
    set({ status: 'idle', currentId: null, progress: 0 });
  },
}));
